import { Box, Stack, Typography } from "@mui/material";
import { ScopeOfWorkAdditionalObjects } from "../../../../../quotes/quote-detail/summary/variants/advanced/components/scope-of-work/ScopeOfWorkAdditionalObjects";
import { ScopeOfWorkAreas } from "../../../../../quotes/quote-detail/summary/variants/advanced/components/scope-of-work/ScopeOfWorkAreas";
import { ScopeOfWorkSpecialRequests } from "../../../../../quotes/quote-detail/summary/variants/advanced/components/scope-of-work/ScopeOfWorkSpecialRequests";
import { ScopeOfWorkTypeOfPlaces } from "../../../../../quotes/quote-detail/summary/variants/advanced/components/scope-of-work/ScopeOfWorkTypeOfPlaces";
import { ScopeOfWorkTypeOfService } from "../../../../../quotes/quote-detail/summary/variants/advanced/components/scope-of-work/ScopeOfWorkTypeOfService";
import { colors, alphaVar } from "@components/ui/colors";
import { ScopeOfWorkOtherAreas } from "../../../../../quotes/quote-detail/summary/variants/advanced/components/scope-of-work/ScopeOfWorkOtherAreas";
type Props = {
  scope: any;
  type?: string;
  jobDescription?: string | null;
};

const getAreaQuantity = (area: any): number => {
  if (area?.quantity != null) {
    const quantity = Number(area.quantity);

    return Number.isFinite(quantity) && quantity > 0 ? quantity : 0;
  }

  const squareFootages = Array.isArray(area?.squareFootages) ? area.squareFootages : [];

  return squareFootages.reduce((total: number, item: any) => total + (Number(item?.quantity) || 0), 0);
};

export const JobScopeOfWorkView = ({ scope, type, jobDescription }: Props) => {
  if (!scope) return null;

  const originalServiceName = scope?.typeOfService?.name?.trim() || scope?.name?.trim() || "Service";

  const serviceName = scope?.serviceNameOverride?.trim() || originalServiceName;

  const hasServiceAlias = Boolean(scope?.serviceNameOverride?.trim());

  const allAreas = Array.isArray(scope?.areasByScopesOfWork) ? scope.areasByScopesOfWork : [];

  const visibleAreas = allAreas.filter((area: any) => {
    return getAreaQuantity(area) > 0 && area?.otherArea !== true;
  });

  const visibleOtherAreas = allAreas.filter((area: any) => {
    return getAreaQuantity(area) > 0 && area?.otherArea === true;
  });

  const hasAreas = visibleAreas.length > 0;
  const hasOtherAreas = visibleOtherAreas.length > 0;

  const hasTypeOfPlace = Boolean(scope?.typeOfPlace?.typeOfPlace);

  const additionalObjects =
    scope?.additionalObjects ?? scope?.additionalObjectUids ?? scope?.additionalObjectsByScopesOfWork ?? scope?.objectsByScopesOfWork ?? [];

  const specialRequests = scope?.specialRequests ?? scope?.specialRequestUids ?? scope?.specialRequestsByScopesOfWork ?? [];

  const hasAdditionalObjects = Array.isArray(additionalObjects) && additionalObjects.length > 0;

  const hasSpecialRequests = Array.isArray(specialRequests) && specialRequests.length > 0;

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100%",
        bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
        px: { xs: 2, sm: 3 },
        py: { xs: 2, sm: 3 },
        overflow: "auto",
      }}
    >
      <Stack
        spacing={2}
        sx={{
          width: "100%",
          maxWidth: 1200,
          mx: "auto",
        }}
      >
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 0.5,
            mt: 1,
            px: 1.25,
            py: 0.6,
            borderRadius: "999px",
            bgcolor: "background.default",
            color: "text.secondary",
            fontSize: 12,
            boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.06)",
            maxWidth: "100%",
          }}
        >
          <Typography
            component="span"
            sx={{
              fontSize: "inherit",
              fontWeight: 700,
              color: "inherit",
              flexShrink: 0,
            }}
          >
            Service alias:
          </Typography>

          <Typography
            component="span"
            sx={{
              fontSize: "inherit",
              fontWeight: 500,
              color: "inherit",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {serviceName}
          </Typography>

          {hasServiceAlias ? (
            <>
              <Typography
                component="span"
                sx={{
                  fontSize: "inherit",
                  color: "inherit",
                  flexShrink: 0,
                }}
              >
                ·
              </Typography>

              <Typography
                component="span"
                sx={{
                  fontSize: "inherit",
                  fontWeight: 700,
                  color: "inherit",
                  flexShrink: 0,
                }}
              >
                Original service name:
              </Typography>

              <Typography
                component="span"
                sx={{
                  fontSize: "inherit",
                  fontWeight: 500,
                  color: "inherit",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {originalServiceName}
              </Typography>
            </>
          ) : null}
        </Box>

        <ScopeOfWorkTypeOfService
          isAdvanced={type === "advanced"}
          name={scope.name}
          typeOfService={scope.typeOfService}
          serviceNameOverride={scope.serviceNameOverride}
          serviceDescriptionOverride={jobDescription}
          costOverride={scope.costOverride}
          frequencyType={scope.frequencyType}
          squareFootage={scope.squareFootage}
        />

        {hasTypeOfPlace ? <ScopeOfWorkTypeOfPlaces typeOfPlaces={scope.typeOfPlace.typeOfPlace} /> : null}
        {hasAreas ? <ScopeOfWorkAreas areas={visibleAreas} /> : null}

        {hasOtherAreas ? <ScopeOfWorkOtherAreas list={visibleOtherAreas} /> : null}

        {hasAdditionalObjects ? <ScopeOfWorkAdditionalObjects list={additionalObjects} /> : null}
        {hasSpecialRequests ? <ScopeOfWorkSpecialRequests list={specialRequests} /> : null}
      </Stack>
    </Box>
  );
};
export const SECTION_STYLES = {
  root: {
    px: { xs: 2, sm: 2.5 },
    py: { xs: 1.75, sm: 2 },
    borderRadius: 3,
    bgcolor: "background.paper",
    boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.06)",
    mb: 2,
  },

  imageCard: {
    height: "100%",
    p: 1.35,
    borderRadius: 3,
    bgcolor: "var(--fx-surface-base, #FFFFFF)",
    boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.06)",
    transition: "all 0.15s ease",
    "&:hover": {
      bgcolor: alphaVar(colors.orange[100], 0.08),
      boxShadow: `inset 0 0 0 1px ${alphaVar(colors.orange[400], 0.22)}`,
      transform: "translateY(-1px)",
    },
  },

  imageBox: {
    width: "100%",
    height: 120,
    borderRadius: 2.5,
    color: colors.orange[700],
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    p: 1.5,
  },

  iconBox: {
    width: 42,
    height: 42,
    borderRadius: 2.5,
    bgcolor: "var(--fx-colors-info-50, #EFF6FF)",
    color: "info.main",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  areaImageBox: {
    width: 50,
    height: 50,
    borderRadius: 2.25,
    color: colors.orange[700],
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    overflow: "hidden",
    boxShadow: `inset 0 0 0 1px ${alphaVar(colors.orange[400], 0.16)}`,
    p: 0.55,
  },

  listItem: {
    p: 1.25,
    borderRadius: 3,
    bgcolor: "var(--fx-surface-base, #FFFFFF)",
    boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.06)",
    transition: "all 0.15s ease",
    "&:hover": {
      boxShadow: `inset 0 0 0 1px ${alphaVar(colors.orange[400], 0.22)}`,
      bgcolor: alphaVar(colors.orange[100], 0.08),
    },
  },

  listHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 1.5,
    cursor: "pointer",
    minWidth: 0,
  },

  subLabel: {
    color: colors.orange[700],
    fontSize: "0.72rem",
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },

  pill: {
    display: "inline-flex",
    alignItems: "center",
    px: 1.25,
    py: 0.55,
    borderRadius: "999px",
    bgcolor: "var(--fx-colors-info-50, #EFF6FF)",
    color: "info.main",
    fontSize: 12,
    fontWeight: 900,
    whiteSpace: "nowrap",
    boxShadow: "inset 0 0 0 1px rgba(25, 118, 210, 0.10)",
  },

  infoPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 0.4,
    px: 0.9,
    py: 0.35,
    borderRadius: "999px",
    bgcolor: alphaVar(colors.orange[100], 0.45),
    color: colors.orange[700],
    fontSize: "0.7rem",
    fontWeight: 900,
    boxShadow: `inset 0 0 0 1px ${alphaVar(colors.orange[400], 0.12)}`,
  },

  infoPillInfo: {
    display: "inline-flex",
    alignItems: "center",
    gap: 0.4,
    px: 0.9,
    py: 0.35,
    borderRadius: "999px",
    bgcolor: "var(--fx-colors-info-50, #EFF6FF)",
    color: "info.main",
    fontSize: "0.7rem",
    fontWeight: 900,
    boxShadow: "inset 0 0 0 1px rgba(25, 118, 210, 0.10)",
  },

  infoPillMuted: {
    display: "inline-flex",
    alignItems: "center",
    gap: 0.4,
    px: 0.9,
    py: 0.35,
    borderRadius: "999px",
    bgcolor: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.035)",
    color: "text.secondary",
    fontSize: "0.7rem",
    fontWeight: 800,
    boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.04)",
  },

  noteBadge: {
    px: 0.8,
    py: 0.25,
    borderRadius: "999px",
    bgcolor: "var(--fx-colors-info-50, #EFF6FF)",
    color: "info.main",
    fontSize: "0.66rem",
    fontWeight: 900,
    whiteSpace: "nowrap",
  },
} as const;
